<template>
  <div class="p-4">

    <!-- ============================================================ -->
    <!-- SECCIÓN 1: Cónyuge / Conviviente                             -->
    <!-- ============================================================ -->
    <div class="family-section">
      <div class="dep-section-header">
        <h6 class="text-muted mb-0">Cónyuge / Conviviente</h6>
        <wz-button v-if="!spouse" variant="primary" size="sm" @click="openCreate('CONYUGE')">
          <i class="ri-add-line mr-1"></i>Registrar cónyuge
        </wz-button>
      </div>

      <div v-if="spouse" class="dep-card-list">
        <dependent-card :dependent="spouse" @edit="openEdit" @deactivate="confirmDeactivate" @docs="openDocs" />
      </div>
      <p v-else class="text-muted mb-0">No hay cónyuge/conviviente registrado</p>
    </div>

    <!-- ============================================================ -->
    <!-- SECCIÓN 2: Padres / Madres                                   -->
    <!-- ============================================================ -->
    <div class="family-section">
      <div class="dep-section-header">
        <h6 class="text-muted mb-0">Padres / Madres</h6>
        <wz-button variant="primary" size="sm" @click="openCreate('OTRO')">
          <i class="ri-add-line mr-1"></i>Agregar padre/madre
        </wz-button>
      </div>

      <div v-if="parents.length > 0" class="dep-card-list">
        <dependent-card
          v-for="parent in parents"
          :key="parent.id"
          :dependent="parent"
          @edit="openEdit"
          @deactivate="confirmDeactivate"
          @docs="openDocs"
        />
      </div>
      <p v-else class="text-muted mb-0">No hay padres registrados</p>
    </div>

    <!-- ============================================================ -->
    <!-- SECCIÓN 3: Hijos                                             -->
    <!-- ============================================================ -->
    <div class="family-section">
      <div class="dep-section-header">
        <h6 class="text-muted mb-0">Hijos</h6>
        <wz-button variant="primary" size="sm" @click="openCreate('HIJO')">
          <i class="ri-add-line mr-1"></i>Agregar hijo
        </wz-button>
      </div>

      <!-- Resumen asignación familiar: arriba de la lista, lo primero que ve RRHH (solo si hay hijos). -->
      <div v-if="children.length > 0" class="mb-3">
        <wz-alert v-if="qualifiedCount > 0" variant="success" icon="ri-check-line" class="mb-0">
          <strong>Este colaborador tiene derecho a asignación familiar.</strong>
        </wz-alert>
        <wz-alert v-else variant="secondary" icon="ri-information-line" class="mb-0">
          <strong>Este colaborador no genera asignación familiar.</strong>
        </wz-alert>
      </div>

      <div v-if="children.length > 0" class="dep-card-list">
        <dependent-card
          v-for="child in children"
          :key="child.id"
          :dependent="child"
          show-child-fields
          @edit="openEdit"
          @deactivate="confirmDeactivate"
          @docs="openDocs"
        />
      </div>
      <p v-else class="text-muted mb-0">No hay hijos registrados</p>
    </div>


    <!-- ============================================================ -->
    <!-- MODAL: Agregar / Editar dependiente                          -->
    <!-- ============================================================ -->
    <wz-modal
      v-model="modalDependent"
      :title="modalTitle"
      size="lg"
      :close-on-overlay="false"
    >
      <div class="flex flex-wrap -mx-2">
        <!-- Tipo de relación (cónyuge/conviviente/padre/madre) -->
        <div class="w-full md:w-1/2 px-2" v-if="modalType !== 'HIJO'">
          <div class="mb-3">
            <label>Tipo de relación <span class="text-danger">*</span></label>
            <wz-select
              :items="otherTypeOptions"
              v-model="modalForm.relationshipType"
            />
          </div>
        </div>

        <!-- Tipo de documento -->
        <div class="w-full md:w-1/2 px-2">
          <div class="mb-3">
            <label>Tipo de documento <span class="text-danger">*</span></label>
            <wz-select
              :items="docTypeOptions"
              v-model="modalForm.documentType"
              defaultOption="Seleccione"
            />
          </div>
        </div>

        <!-- Número de documento -->
        <div class="w-full md:w-1/2 px-2">
          <div class="mb-3">
            <label>Número de documento <span class="text-danger">*</span></label>
            <input type="text" v-model="modalForm.documentNumber" class="wz-form-control" placeholder="Número de documento" />
          </div>
        </div>

        <!-- Nombres -->
        <div class="w-full md:w-1/2 px-2">
          <div class="mb-3">
            <label>Nombres <span class="text-danger">*</span></label>
            <input type="text" v-model="modalForm.firstName" class="wz-form-control" placeholder="Nombres" />
          </div>
        </div>

        <!-- Apellido paterno -->
        <div class="w-full md:w-1/2 px-2">
          <div class="mb-3">
            <label>Apellido paterno <span class="text-danger">*</span></label>
            <input type="text" v-model="modalForm.lastNamePaternal" class="wz-form-control" placeholder="Apellido paterno" />
            <wz-alert
              v-if="modalType === 'HIJO' && childPaternalMismatch"
              variant="warning"
              icon="ri-error-warning-line"
              class="mt-2 mb-0"
              style="font-size: 12px; padding: 8px 12px;"
            >
              El apellido paterno del hijo no coincide con el del padre. Verifique antes de guardar.
            </wz-alert>
          </div>
        </div>

        <!-- Apellido materno -->
        <div class="w-full md:w-1/2 px-2">
          <div class="mb-3">
            <label>Apellido materno</label>
            <input type="text" v-model="modalForm.lastNameMaternal" class="wz-form-control" placeholder="Apellido materno" />
            <wz-alert
              v-if="modalType === 'HIJO' && childMaternalMismatch"
              variant="warning"
              icon="ri-error-warning-line"
              class="mt-2 mb-0"
              style="font-size: 12px; padding: 8px 12px;"
            >
              El apellido materno del hijo no coincide con el apellido paterno de la madre. Verifique antes de guardar.
            </wz-alert>
          </div>
        </div>

        <!-- Fecha de nacimiento -->
        <div class="w-full md:w-1/2 px-2">
          <div class="mb-3">
            <label>Fecha de nacimiento <span class="text-danger">*</span></label>
            <wz-datepicker v-model="modalForm.birthDate" />
            <small v-if="modalAge !== null" class="text-muted">{{ modalAge }} años</small>
          </div>
        </div>

        <!-- Género -->
        <div class="w-full md:w-1/2 px-2">
          <div class="mb-3">
            <label>Género</label>
            <wz-select
              :items="genderOptions"
              v-model="modalForm.gender"
              defaultOption="Seleccione"
            />
          </div>
        </div>

        <!-- Campos extra para HIJO -->
        <template v-if="modalType === 'HIJO'">
          <!-- Constancia de estudios (solo si edad >= 18) -->
          <div class="w-full md:w-1/2 px-2" v-if="modalAge !== null && modalAge >= 18">
            <div class="mb-3">
              <label>
                <input type="checkbox" v-model="modalForm.hasStudyCertificate" class="mr-1" />
                Constancia de estudios
              </label>
            </div>
          </div>

          <!-- Vencimiento constancia -->
          <div class="w-full md:w-1/2 px-2" v-if="modalForm.hasStudyCertificate && modalAge >= 18">
            <div class="mb-3">
              <label>Vencimiento de constancia <span class="text-danger">*</span></label>
              <wz-datepicker v-model="modalForm.studyCertificateExpiry" />
            </div>
          </div>

          <!-- Discapacidad -->
          <div class="w-full md:w-1/2 px-2">
            <div class="mb-3">
              <label>
                <input type="checkbox" v-model="modalForm.hasDisability" class="mr-1" />
                Tiene discapacidad
              </label>
            </div>
          </div>
        </template>

        <!-- Documento sustentatorio del vínculo (opcional; típico en cónyuge/conviviente) -->
        <template v-if="modalType !== 'HIJO'">
          <div class="w-full md:w-1/2 px-2">
            <div class="mb-3">
              <label>Tipo de documento sustentatorio</label>
              <input type="text" v-model="modalForm.supportingDocumentType" class="wz-form-control"
                     placeholder="Ej: Partida de matrimonio / Acta de convivencia" />
            </div>
          </div>
          <div class="w-full md:w-1/2 px-2">
            <div class="mb-3">
              <label>Número de documento sustentatorio</label>
              <input type="text" v-model="modalForm.supportingDocumentNumber" class="wz-form-control"
                     placeholder="Número (opcional)" />
            </div>
          </div>
        </template>

        <!-- Observaciones -->
        <div class="w-full px-2">
          <div class="mb-3">
            <label>Observaciones</label>
            <wz-textarea v-model="modalForm.notes" rows="2" placeholder="Observaciones (opcional)" />
          </div>
        </div>
      </div>
      <template #footer>
        <wz-button variant="outline-secondary" @click="modalDependent = false">Cancelar</wz-button>
        <wz-button :disabled="!canSaveModal" @click="saveDependent">Guardar</wz-button>
      </template>
    </wz-modal>

    <!-- Confirmar desactivación -->
    <wz-confirm-modal
      v-model="deactivateModal"
      title="Desactivar dependiente"
      :description="deactivateMessage"
      accept-text="Desactivar"
      accept-variant="danger"
      @confirm="onDeactivateConfirmed"
    />

    <!-- ============================================================ -->
    <!-- MODAL: Documentos del dependiente                            -->
    <!-- ============================================================ -->
    <wz-modal
      v-model="docModal"
      :title="docModalTitle"
      size="xl"
      :close-on-overlay="false"
    >
      <!-- Toolbar: toggle del formulario de subida -->
      <div class="flex justify-end mb-3">
        <wz-button variant="primary" @click="showDocUploadForm = !showDocUploadForm">
          <i :class="showDocUploadForm ? 'ri-close-line' : 'ri-upload-2-line'" class="mr-1"></i>
          {{ showDocUploadForm ? 'Cancelar' : 'Subir documento' }}
        </wz-button>
      </div>

      <!-- Formulario de subida (oculto por defecto) -->
      <div v-if="showDocUploadForm" class="border rounded p-3 mb-4">
        <h6 class="text-muted mb-3">Subir documento</h6>

        <div class="mb-3">
          <label>Tipo de documento <span class="text-danger">*</span></label>
          <wz-select
            :items="documentTypeList"
            v-model="docUploadForm.documentTypeId"
            defaultOption="Elegir tipo de documento"
          />
        </div>

        <div class="mb-3">
          <label>Archivo <span class="text-danger">*</span> <small class="text-muted">(PDF, JPG, JPEG, PNG — máx. 25MB)</small></label>
          <wz-file-input
            :key="docFileKey"
            class="w-full mt-2"
            id-file="dependent-doc-upload"
            :accept-file="['.pdf', '.jpg', '.jpeg', '.png']"
            @onChange="onDocFileChange"
          />
        </div>

        <div class="mb-3">
          <label>Observaciones</label>
          <textarea
            v-model="docUploadForm.notes"
            class="wz-form-control w-full"
            rows="2"
            placeholder="Ingresar observaciones (opcional)"
          />
        </div>

        <div class="flex justify-end">
          <wz-button variant="primary" :disabled="docUploading" @click="uploadDependentDocument">
            {{ docUploading ? 'Subiendo...' : 'Guardar documento' }}
          </wz-button>
        </div>
      </div>

      <!-- Lista de documentos -->
      <table class="wz-table w-full">
        <thead>
          <tr>
            <th>Tipo</th>
            <th>Archivo</th>
            <th>Observaciones</th>
            <th class="text-center">Opciones</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="dependentDocuments.length === 0">
            <td colspan="4" class="text-center text-muted py-3">Sin documentos</td>
          </tr>
          <tr v-for="doc in dependentDocuments" :key="doc.id">
            <td>{{ doc.documentType ? doc.documentType.name : '-' }}</td>
            <td :title="doc.fileName">{{ truncateFileName(doc.fileName) }}</td>
            <td>{{ doc.notes || '-' }}</td>
            <td class="text-center">
              <wz-tooltip text="Descargar">
                <wz-button variant="outline" size="sm" class="mr-1" @click="downloadDependentDoc(doc)">
                  <i class="ri-download-2-line"></i>
                </wz-button>
              </wz-tooltip>
              <wz-tooltip text="Eliminar">
                <wz-button variant="outline-danger" size="sm" @click="confirmDeleteDoc(doc)">
                  <i class="ri-delete-bin-line"></i>
                </wz-button>
              </wz-tooltip>
            </td>
          </tr>
        </tbody>
      </table>

      <template #footer>
        <wz-button variant="outline-secondary" @click="docModal = false">Cerrar</wz-button>
      </template>
    </wz-modal>

    <!-- Confirmar eliminación de documento -->
    <wz-confirm-modal
      v-model="docDeleteModal"
      title="Eliminar documento"
      :description="docDeleteTarget ? `¿Eliminar el documento '${docDeleteTarget.fileName}'?` : ''"
      accept-text="Eliminar"
      accept-variant="danger"
      @confirm="deleteDependentDoc"
    />

  </div>
</template>

<script>
import { Alert } from "@wizepe/wizeui"
import { EmployeeDependentService, EmployeeDocumentService, DocumentTypeService } from '@/core/services'
import { Constants } from '@/core/utils'
import DependentCard from '@/modules/Employees/components/DependentCard.vue'

export default {
  name: 'FamilyTab',
  components: { DependentCard },
  
  props: {
    employeeId: { type: Number, required: true },
    // Apellido paterno del colaborador, para alertar (no bloquear) si un hijo no lo comparte.
    employeePaternalSurname: { type: String, default: '' },
    // Género del colaborador (1 = masculino, 2 = femenino) para elegir la referencia correcta
    // del apellido paterno/materno del hijo. null/otro → fallback (ver computeds).
    employeeGender: { type: [Number, String], default: null },
  },
  data() {
    return {
      dependents: [],
      editingDependent: null,
      modalType: 'HIJO',
      modalForm: this.emptyForm(),
      spouseTypeOptions: [
        { key: 'CONYUGE',     label: 'Cónyuge' },
        { key: 'CONVIVIENTE', label: 'Conviviente' },
      ],
      // Opciones del modal para familiares que no son hijos (incluye padre/madre).
      otherTypeOptions: [
        { key: 'CONYUGE',     label: 'Cónyuge' },
        { key: 'CONVIVIENTE', label: 'Conviviente' },
        { key: 'PADRE',       label: 'Padre' },
        { key: 'MADRE',       label: 'Madre' },
      ],
      docTypeOptions: [
        Constants.IdentifierType.DNI,
        Constants.IdentifierType.CE,
        Constants.IdentifierType.PASAPORTE,
      ],
      genderOptions: [
        { key: 'M', label: 'Masculino' },
        { key: 'F', label: 'Femenino' },
      ],
      modalDependent: false,
      deactivateModal: false,
      deactivateTarget: null,
      // Documentos del dependiente
      docModal: false,
      docDependent: null,
      dependentDocuments: [],
      documentTypeList: [],
      docUploadForm: { documentTypeId: null, file: null, notes: null },
      docFileKey: 0,
      docUploading: false,
      showDocUploadForm: false,
      docDeleteModal: false,
      docDeleteTarget: null,
    }
  },
  computed: {
    spouse() {
      return this.dependents.find(d =>
        d.relationshipType === 'CONYUGE' || d.relationshipType === 'CONVIVIENTE'
      ) || null
    },
    // Padres/madres del colaborador (visibles en su propia sección).
    parents() {
      return this.dependents
        .filter(d => d.relationshipType === 'PADRE' || d.relationshipType === 'MADRE')
        .sort((a, b) => (a.relationshipType || '').localeCompare(b.relationshipType || ''))
    },
    // ── Alertas preventivas (no bloqueantes) de apellidos del HIJO ──────────────────────────────
    // En Perú el apellido PATERNO del hijo proviene del padre y el MATERNO es el paterno de la madre.
    // Según el género del colaborador se elige la referencia correcta; si no hay referencia
    // disponible para un apellido, no se alerta ese específico. Insensible a mayúsculas/acentos/espacios.

    // Referencias esperadas para el apellido PATERNO del hijo.
    paternalReferences() {
      const g = Number(this.employeeGender)
      const own = this.normSurname(this.employeePaternalSurname)
      const spouse = this.normSurname(this.spouse && this.spouse.lastNamePaternal)
      let refs
      if (g === 2) {          // MUJER: el paterno del hijo viene del cónyuge (padre)
        refs = [spouse]
      } else if (g === 1) {   // HOMBRE: su propio paterno + el del cónyuge (si existe)
        refs = [own, spouse]
      } else {                // Género desconocido → fallback: comportamiento previo (paterno + cónyuge)
        refs = [own, spouse]
      }
      return refs.filter(Boolean)
    },
    // Referencias esperadas para el apellido MATERNO del hijo (= apellido paterno de la madre).
    maternalReferences() {
      const g = Number(this.employeeGender)
      const own = this.normSurname(this.employeePaternalSurname)
      const spouse = this.normSurname(this.spouse && this.spouse.lastNamePaternal)
      let refs
      if (g === 2) {          // MUJER: el materno del hijo = su propio paterno
        refs = [own]
      } else if (g === 1) {   // HOMBRE: el materno del hijo = paterno del cónyuge (si existe)
        refs = [spouse]
      } else {                // Género desconocido → no validar materno
        refs = []
      }
      return refs.filter(Boolean)
    },
    childPaternalMismatch() {
      if (this.modalType !== 'HIJO') return false
      const child = this.normSurname(this.modalForm.lastNamePaternal)
      if (!child) return false
      const refs = this.paternalReferences
      if (refs.length === 0) return false
      return !refs.includes(child)
    },
    childMaternalMismatch() {
      if (this.modalType !== 'HIJO') return false
      const child = this.normSurname(this.modalForm.lastNameMaternal)
      if (!child) return false
      const refs = this.maternalReferences
      if (refs.length === 0) return false
      return !refs.includes(child)
    },
    children() {
      return this.dependents
        .filter(d => d.relationshipType === 'HIJO')
        .sort((a, b) => (a.age || 0) - (b.age || 0))
    },
    qualifiedCount() {
      return this.children.filter(c =>
        c.familyAllowanceStatus === 'CALIFICA' || c.familyAllowanceStatus === 'CONSTANCIA_POR_VENCER'
      ).length
    },
    modalTitle() {
      if (this.modalType === 'HIJO') {
        return this.editingDependent ? 'Editar hijo' : 'Registrar hijo'
      }
      return this.editingDependent ? 'Editar familiar' : 'Registrar familiar'
    },
    modalAge() {
      if (!this.modalForm.birthDate) return null
      const birth = new Date(this.modalForm.birthDate + 'T00:00:00')
      const today = new Date()
      let age = today.getFullYear() - birth.getFullYear()
      const m = today.getMonth() - birth.getMonth()
      if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--
      return age >= 0 ? age : 0
    },
    canSaveModal() {
      const f = this.modalForm
      if (!f.documentType || !f.documentNumber || !f.firstName || !f.lastNamePaternal || !f.birthDate) return false
      if (this.modalType !== 'HIJO' && !f.relationshipType) return false
      if (this.modalType === 'HIJO' && f.hasStudyCertificate && this.modalAge >= 18 && !f.studyCertificateExpiry) return false
      return true
    },
    deactivateMessage() {
      if (!this.deactivateTarget) return ''
      const name = this.deactivateTarget.fullName || this.deactivateTarget.firstName
      return `¿Está seguro que desea desactivar a ${name}?`
    },
    docModalTitle() {
      if (!this.docDependent) return 'Documentos'
      const name = this.docDependent.fullName
        || `${this.docDependent.firstName || ''} ${this.docDependent.lastNamePaternal || ''}`.trim()
      return `Documentos de ${name}`
    },
  },
  watch: {
    'modalForm.hasStudyCertificate'(val) {
      if (!val) this.modalForm.studyCertificateExpiry = null
    },
  },
  methods: {
    // Normaliza un apellido para comparar: sin espacios extremos, mayúsculas y sin acentos.
    normSurname(s) {
      return (s || '').trim().toUpperCase().normalize('NFD')
        .split('').filter(c => c.charCodeAt(0) < 768 || c.charCodeAt(0) > 879).join('')
    },

    emptyForm() {
      return {
        relationshipType: null,
        documentType: null,
        documentNumber: null,
        firstName: null,
        lastNamePaternal: null,
        lastNameMaternal: null,
        birthDate: null,
        gender: null,
        hasStudyCertificate: false,
        studyCertificateExpiry: null,
        hasDisability: false,
        supportingDocumentType: null,
        supportingDocumentNumber: null,
        notes: null,
      }
    },

    async loadDependents() {
      try {
        const resp = await EmployeeDependentService.getByEmployee(this.employeeId)
        this.dependents = resp.payload || []
      } catch (e) {
        console.error('Error loading dependents', e)
      }
    },

    openCreate(type) {
      this.editingDependent = null
      this.modalType = type
      this.modalForm = this.emptyForm()
      if (type === 'HIJO') {
        this.modalForm.relationshipType = 'HIJO'
      } else if (type === 'OTRO') {
        this.modalForm.relationshipType = 'PADRE'
      } else {
        this.modalForm.relationshipType = 'CONYUGE'
      }
      this.modalDependent = true
    },

    openEdit(dep) {
      this.editingDependent = dep
      this.modalType = dep.relationshipType === 'HIJO' ? 'HIJO' : 'OTRO'
      this.modalForm = {
        relationshipType: dep.relationshipType,
        documentType: dep.documentType,
        documentNumber: dep.documentNumber,
        firstName: dep.firstName,
        lastNamePaternal: dep.lastNamePaternal,
        lastNameMaternal: dep.lastNameMaternal,
        birthDate: dep.birthDate,
        gender: dep.gender,
        hasStudyCertificate: dep.hasStudyCertificate || false,
        studyCertificateExpiry: dep.studyCertificateExpiry,
        hasDisability: dep.hasDisability || false,
        supportingDocumentType: dep.supportingDocumentType,
        supportingDocumentNumber: dep.supportingDocumentNumber,
        notes: dep.notes,
      }
      this.modalDependent = true
    },

    async saveDependent() {
      if (!this.canSaveModal) return
      try {
        this.$store.dispatch('app/loading', true)
        const data = { ...this.modalForm }
        if (this.modalType === 'HIJO') {
          data.relationshipType = 'HIJO'
        }
        if (!data.hasStudyCertificate) {
          data.studyCertificateExpiry = null
        }
        if (this.editingDependent) {
          await EmployeeDependentService.update(this.employeeId, this.editingDependent.id, data)
          Alert.success('Dependiente actualizado correctamente')
        } else {
          await EmployeeDependentService.create(this.employeeId, data)
          Alert.success('Dependiente registrado correctamente')
        }
        this.modalDependent = false
        await this.loadDependents()
      } catch (e) {
        Alert.error(e.errors ? e.errors.message : 'Error al guardar el dependiente')
      } finally {
        this.$store.dispatch('app/loading', false)
      }
    },

    confirmDeactivate(dep) {
      this.deactivateTarget = dep
      this.deactivateModal = true
    },

    async onDeactivateConfirmed() {
      const dep = this.deactivateTarget
      this.deactivateModal = false
      if (!dep) return
      try {
        this.$store.dispatch('app/loading', true)
        await EmployeeDependentService.deactivate(this.employeeId, dep.id)
        Alert.success('Dependiente desactivado')
        await this.loadDependents()
      } catch (e) {
        Alert.error(e.errors ? e.errors.message : 'Error al desactivar')
      } finally {
        this.$store.dispatch('app/loading', false)
        this.deactivateTarget = null
      }
    },

    // --- Documentos del dependiente ---

    async openDocs(dep) {
      this.docDependent = dep
      this.docUploadForm = { documentTypeId: null, file: null, notes: null }
      this.docFileKey++
      this.showDocUploadForm = false
      this.docModal = true
      await this.loadDependentDocuments()
    },

    async loadDocumentTypes() {
      try {
        const resp = (await DocumentTypeService.list()).payload || []
        this.documentTypeList = resp.map(d => ({ key: d.id, label: d.name }))
      } catch (e) {
        console.error('Error loading document types', e)
      }
    },

    async loadDependentDocuments() {
      try {
        this.dependentDocuments = (await EmployeeDocumentService.list(this.employeeId, this.docDependent.id)).payload || []
      } catch (e) {
        console.error('Error loading dependent documents', e)
        this.dependentDocuments = []
      }
    },

    onDocFileChange(files) {
      this.docUploadForm.file = files && files.length ? files[0] : null
    },

    async uploadDependentDocument() {
      if (!this.docUploadForm.documentTypeId) { Alert.error('Selecciona el tipo de documento'); return }
      if (!this.docUploadForm.file) { Alert.error('Selecciona un archivo'); return }
      try {
        this.docUploading = true
        const formData = new FormData()
        formData.append('employeeId', this.employeeId)
        formData.append('dependentId', this.docDependent.id)
        formData.append('documentTypeId', this.docUploadForm.documentTypeId)
        formData.append('file', this.docUploadForm.file)
        if (this.docUploadForm.notes) formData.append('notes', this.docUploadForm.notes)
        await EmployeeDocumentService.upload(formData)
        Alert.success('Documento subido correctamente')
        this.docUploadForm = { documentTypeId: null, file: null, notes: null }
        this.docFileKey++
        this.showDocUploadForm = false
        await this.loadDependentDocuments()
      } catch (e) {
        Alert.error(e.errors ? e.errors.message : 'Error al subir el documento')
      } finally {
        this.docUploading = false
      }
    },

    async downloadDependentDoc(doc) {
      try {
        const response = await EmployeeDocumentService.download(doc.id)
        const url = window.URL.createObjectURL(response.data)
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', doc.fileName || 'documento')
        document.body.appendChild(link)
        link.click()
        link.remove()
        window.URL.revokeObjectURL(url)
      } catch (e) {
        Alert.error('Error al descargar el documento')
      }
    },

    confirmDeleteDoc(doc) {
      this.docDeleteTarget = doc
      this.docDeleteModal = true
    },

    async deleteDependentDoc() {
      const doc = this.docDeleteTarget
      this.docDeleteModal = false
      if (!doc) return
      try {
        this.$store.dispatch('app/loading', true)
        await EmployeeDocumentService.delete(doc.id)
        Alert.success('Documento eliminado')
        await this.loadDependentDocuments()
      } catch (e) {
        Alert.error(e.errors ? e.errors.message : 'Error al eliminar el documento')
      } finally {
        this.$store.dispatch('app/loading', false)
        this.docDeleteTarget = null
      }
    },

    // --- Helpers de formato ---

    truncateFileName(name, max = 32) {
      if (!name) return ''
      return name.length > max ? name.slice(0, max) + '...' : name
    },

  },

  created() {
    this.loadDependents()
    this.loadDocumentTypes()
  },
}
</script>

<style scoped>
/* Cada sección separada por un borde superior + padding, en lugar de <hr> pegado a los botones.
   Así el encabezado (y su botón) siempre queda con aire respecto a la línea divisoria. */
.family-section {
  padding-top: 20px;
  margin-top: 20px;
  border-top: 1px solid #e9ecef;
}
.family-section:first-child {
  padding-top: 0;
  margin-top: 0;
  border-top: none;
}

/* Encabezado de sección uniforme en las 3 secciones: título a la izquierda, botón a la derecha,
   con separación (gap) y wrap para que el botón no se apriete ni se pegue al título en anchos chicos. */
.dep-section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px 16px;
  flex-wrap: wrap;
  margin-bottom: 14px;
}

/* Cards de dependientes apiladas con espaciado consistente (las 3 secciones). */
.dep-card-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
</style>
